import { action } from '@/lib/api';
import { postRunAppAction } from '@/redux/actions/gravityActions.js';
import { store } from '@/redux/lib/configureStore';
import selectors from '@/redux/selectors/selectors';
import sendToast from '@/lib/sendToast.js';

const pendingLookupRequests = new Map();

const NETSUITE_COLUMNS_COLLECT_STEP_REQUIRED = 'NETSUITE_COLUMNS_COLLECT_STEP_REQUIRED';

const getStableJson = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(getStableJson).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${getStableJson(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

const buildLookupRequestKey = ({ actionId, appId, appVersion, stepId, credentialId, companyId, args, options, lookupSettings, cache, isTutorial, useAdminCredentials }) => {
  return getStableJson({
    actionId,
    appId,
    appVersion,
    stepId,
    credentialId,
    companyId,
    args: args || {},
    options: options || {},
    lookupSettings,
    cache,
    isTutorial,
    useAdminCredentials
  });
};

const notifyLookupResponse = (response) => {
  if (response?.data?.meta?.code !== NETSUITE_COLUMNS_COLLECT_STEP_REQUIRED) {
    return response;
  }

  sendToast({
    id: 'netsuite-columns-collect-step-required',
    type: 'warning',
    duration: 6000,
    message: 'NetSuite columns are stale. Go to the Deploy tab and re-run the collect step.'
  });

  return response;
};

/**
 * Intended to be used in the context of a app screen, runs a single
 * action for a given app.
 */
const runLookupAction = ({ lookupId, actionId, appId, appVersion, stepId, credentialId, companyId, arguments: args, options = {}, lookupSettings, callback, cache, force = false, isTutorial = false }) => {
  const state = store.getState();
  const paramAppId = selectors.param(state, 'appId');
  const paramAppVersion = selectors.param(state, 'version');
  const isAppScreen = state?.router?.location?.pathname?.includes('/apps');

  const lookupValue = state?.workflow?.workflowLookups[lookupId]?.data;
  if (!force && lookupValue) {
    return;
  }

  action('SET_WORKFLOW_LOOKUP', { id: lookupId, data: [] });

  const resolvedAppId = appId || paramAppId;
  const resolvedAppVersion = appVersion === undefined ? paramAppVersion : appVersion;
  const useAdminCredentials = !companyId ? isAppScreen : false;
  const requestKey = buildLookupRequestKey({
    actionId,
    appId: resolvedAppId,
    appVersion: resolvedAppVersion,
    stepId,
    credentialId,
    companyId,
    args,
    options,
    lookupSettings,
    cache,
    isTutorial,
    useAdminCredentials
  });

  if (pendingLookupRequests.has(requestKey)) {
    pendingLookupRequests.get(requestKey).callbacks.push(callback);
    return pendingLookupRequests.get(requestKey).request;
  }

  const callbacks = [callback];

  const request = postRunAppAction(
    {
      appId: resolvedAppId,
      appVersion: resolvedAppVersion,
      stepId,
      credentialId,
      companyId,
      arguments: args || {},
      options,
      lookup: true,
      lookupSettings,
      cache,
      actionId,
      transform: true,
      useAdminCredentials,
      isTutorial
    },
    (...callbackArgs) => {
      callbacks.forEach((pendingCallback) => {
        pendingCallback?.(...callbackArgs);
      });
    },
    notifyLookupResponse
  );

  pendingLookupRequests.set(requestKey, { callbacks, request });
  request.finally(() => {
    pendingLookupRequests.delete(requestKey);
  });

  return request;
};

export default runLookupAction;
